import type {
  EvaluationRun,
  QueueEvaluationRunBody,
} from '../types/evaluationRun.js';

/**
 * Optional filters/paging for listing evaluation runs. The server only
 * supports `startswith(displayName, '...')` for filtering; we lean on that
 * to recover "runs for template X" by setting displayName = template.name
 * at queue time. Server-side filter on templateName is a future addition.
 */
export interface ListEvaluationRunsOptions {
  /** Returns runs whose displayName starts with this prefix. */
  displayNamePrefix?: string;
  /** Page size; clamped server-side to MaxPageSize. */
  top?: number;
  /** Pagination offset. */
  skip?: number;
}

export interface IEvaluationRunsService {
  queue(
    workspaceName: string,
    body: QueueEvaluationRunBody,
  ): Promise<EvaluationRun>;
  get(workspaceName: string, id: string): Promise<EvaluationRun>;
  cancel(workspaceName: string, id: string): Promise<EvaluationRun>;
  list(
    workspaceName: string,
    options?: ListEvaluationRunsOptions,
  ): Promise<EvaluationRun[]>;
  /**
   * Fetch the server-rendered HTML scorecard report for one or more runs.
   * The server returns `text/html`; callers display it (e.g. in a sandboxed
   * iframe). Passing 2 ids renders a side-by-side comparison.
   */
  getReportHtml(workspaceName: string, ids: string[]): Promise<string>;
}

export class EvaluationRunNotFoundError extends Error {
  readonly code = 'EvaluationRunNotFound';
  constructor(workspaceName: string, id: string) {
    super(`Evaluation run '${id}' not found in workspace '${workspaceName}'.`);
  }
}
